import { memo, useEffect, useMemo, useRef } from 'react';
import {
  ActivityStatusEnum,
  Chat,
  EventTypeEnum,
  FileTypeEnum,
  LocalNotification,
  Packet,
  SendStatusEnum,
  SoundEnum,
  translateChatToNotification,
  UserState,
} from '@matap/shared';
import { MEDIA_CONFIGS, SocketStateEnum } from '@/constants';
import { actionAddNewChatMessage, actionDeleteChat, actionSetConversations, actionSetSocketState, actionSetUserState, actionShowAlert, actionUpdateChat } from '@/redux';
import { useAppDispatch, useAppSelector } from '@/hooks/use.redux';
import { useActiveVisit } from '@/hooks/use.active.visit';
import { useNetState } from '@/hooks/use.net.state';
import { useUser } from '@/hooks/use.user';
import { useRunIf } from '@/hooks/use.run.if';
import { useSocketListener } from '@/hooks/use.socket.listener';

import { useSocket } from '@/hooks/use.socket';
import { useActivityStatus } from '@/hooks/useActivityStatus';
import { getDeviceProfile } from '@/webrtc/utils/utils';
import { canPlayLocalSound, playLocalSound } from '../utils/play.local.sound';
import { useIsForeground } from '@/hooks/use.is.foreground';
import { socket, socketConfig } from './socket';
import { triggerLocalNotification } from './notification-provider/trigger.local.notification';

const TAG = '[socket-provider]';

function BaseSocketProvider({ children }) {
  const dispatch = useAppDispatch();
  const { authenticated } = useUser();
  const socketState = useAppSelector((state) => state.chatReducer.socketState);
  const { activeVisitId } = useActiveVisit();
  const { isNetworkAvailable } = useNetState();
  const { setRemoteActivityStatus } = useActivityStatus();
  const { emit } = useSocket();
  const { userId } = useUser();
  const { isForeground } = useIsForeground();

  const canConnect = useMemo(() => authenticated && isNetworkAvailable, [authenticated, isNetworkAvailable]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!authenticated) {
      socket.disconnect();
    }
  }, [authenticated]);

  useRunIf(() => {
    dispatch(actionSetSocketState(SocketStateEnum.CONNECTING));
    getDeviceProfile().then((res) => {
      (socket.auth as any).deviceProfile = res;
      socket.connect();
    });
  }, [canConnect, socketState === SocketStateEnum.INITIAL]);

  useRunIf(() => {
    if (!canConnect) return;
    dispatch(actionSetSocketState(SocketStateEnum.RECONNECTING));
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
    }
    reconnectTimerRef.current = setTimeout(() => {
      if (!canConnect) return;
      socket.connect();
    }, 1000);
  }, [socketState === SocketStateEnum.DISCONNECTED, canConnect]);

  useRunIf(() => {
    dispatch(actionSetSocketState(SocketStateEnum.DISCONNECTED));
  }, [socketState === SocketStateEnum.CONNECT_ERROR]);

  useEffect(() => {
    const onOpening = () => dispatch(actionSetSocketState(SocketStateEnum.CONNECTING));
    const onConnecting = () => dispatch(actionSetSocketState(SocketStateEnum.CONNECTING));
    const onDisconnect = () => dispatch(actionSetSocketState(SocketStateEnum.DISCONNECTED));
    const onAuthenticated = () => {
      dispatch(actionSetSocketState(SocketStateEnum.CONNECTED));
    };
    const onConnectError = () => {
      dispatch(actionSetSocketState(SocketStateEnum.CONNECT_ERROR));
    };

    socket
      .on('open', onOpening)
      .on('connecting', onConnecting)
      .on('disconnect', onDisconnect)
      .on('authenticated', onAuthenticated)
      .on('error', (e) => {
        console.error(TAG, e);
        onDisconnect();
      })
      .on('connect_error', (e) => {
        console.error(TAG, e);
        onConnectError();
      })
      .on(EventTypeEnum.STATE_UPDATE, (packet: Packet<UserState>) => {
        dispatch(actionSetUserState(packet.payload));
      })
      .on(EventTypeEnum.MEDIA_CONFIGS, (config) => {
        Object.assign(MEDIA_CONFIGS, config);
      })
      .on(EventTypeEnum.DISPLAY_MESSAGE, (packet: Packet<{ message: string; type: 'fancy' | 'quick'; mode: 'error' | 'warn' | 'info' }>) => {
        dispatch(actionShowAlert({ message: packet.payload.message, type: packet.payload.type, mode: packet.payload.mode }));
      });
  }, [dispatch]);

  useSocketListener(
    EventTypeEnum.MESSAGE,
    (packet: Packet<Chat>) => {
      if (packet.payload.senderId !== userId) {
        emit(EventTypeEnum.MESSAGE_ACK, { visitId: packet.visitId, payload: { _id: packet.payload._id } });

        if (!isForeground) {
          const { title, body } = translateChatToNotification(packet.payload);
          body && triggerLocalNotification({ title, body });
        }
      }
      dispatch(actionAddNewChatMessage(packet.payload));
    },
    [userId, isForeground],
  );

  useSocketListener(
    EventTypeEnum.CONVERSATIONS,
    (packet: Packet<Chat[]>) => {
      packet.payload
        .filter((c) => c.sendStatus !== SendStatusEnum.DELIVERED && c.senderId !== userId)
        .forEach((chat) => socket.emit(EventTypeEnum.MESSAGE_ACK, { visitId: packet.visitId, payload: { _id: chat._id } } as Packet<{ _id: string }>));
      dispatch(actionSetConversations({ chats: packet.payload, userId: userId }));
    },
    [userId],
  );

  useSocketListener(
    EventTypeEnum.ACTIVITY_STATUS,
    (packet: Packet<{ status: ActivityStatusEnum }>) => {
      if (!activeVisitId || packet.visitId !== activeVisitId) {
        return;
      }
      setRemoteActivityStatus(packet.payload.status);
    },
    [activeVisitId],
  );

  useSocketListener(
    EventTypeEnum.UPDATE_CHAT,
    (packet: Packet<{ chat: Partial<Chat & { _id: string }> }>) => {
      if (!activeVisitId || packet.visitId !== activeVisitId) {
        return;
      }
      dispatch(actionUpdateChat(packet.payload));
    },
    [dispatch, activeVisitId],
  );

  useSocketListener(
    EventTypeEnum.DELETE_CHAT,
    (packet: Packet<{ _id: string }>) => {
      if (!activeVisitId || packet.visitId !== activeVisitId) {
        return;
      }
      dispatch(actionDeleteChat(packet.payload._id));
    },
    [dispatch, activeVisitId],
  );

  useSocketListener(
    EventTypeEnum.SERVER_TIME,
    (packet: Packet<number>) => {
      global.timeDifference = packet.payload - Date.now();
      console.log('time difference', global.timeDifference);
    },
    [dispatch, activeVisitId],
  );

  useSocketListener(
    EventTypeEnum.PLAY_SOUND,
    (packet: Packet<{ sound: SoundEnum; ignoreInForeground: boolean }>) => {
      if (packet.payload.ignoreInForeground && isForeground) {
        return;
      }
      if (!canPlayLocalSound()) {
        return false;
      }
      playLocalSound(packet.payload.sound);
      return true; //server ack
    },
    [dispatch, activeVisitId, isForeground],
  );

  useSocketListener(
    EventTypeEnum.LOCAL_NOTIFICATION,
    (packet: Packet<LocalNotification>) => {
      if (packet.payload.separateSound && !canPlayLocalSound()) {
        return false;
      }
      triggerLocalNotification(packet.payload);
      return true; //server ack
    },
    [dispatch, activeVisitId],
  );

  return children;
}

export const SocketProvider = memo(BaseSocketProvider);
